/**
 * Room detail · Current tenant tab.
 *
 * Shows the active booking (if any). The tenant name links to the full
 * tenant profile; a per-tenant actions menu (shared TenantActionsMenu)
 * exposes Act as tenant, Reassign room and Archive. Falls back to a
 * vacant-state empty card when the room has no current tenant.
 *
 * STATUS — this card shows the DERIVED journey stage (useTenantJourneys), the
 * same one the tenant profile header shows, never `bookings.status` or
 * `contract_status`. Those are claims an admin or a flow can write; the stage
 * is worked out from what is actually on file. Rendering the columns here is
 * what made a room read "Active / signed" for a tenant the profile placed at
 * "Key pickup pending". Missing personal info rides along with the badge for
 * the same reason: it is usually why the tenant is stuck at that stage.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, TriangleAlert, UserRound } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { TenantJourneyStageBadge } from "@/components/admin/v2/tenant/TenantJourneyStageBadge";
import { ReassignRoomDialog } from "@/components/admin/v2/tenant/ReassignRoomDialog";
import { TenantActionsMenu } from "@/components/admin/v2/tenant/TenantActionsMenu";
import {
  roomOccupancyPhase,
  useProfile,
  type BookingRow,
} from "@/hooks/v2/useProperties";
import { useTenantJourney } from "@/hooks/v2/useTenantJourneys";
import { tenantInfoGapLabel } from "@/lib/tenantInfoGaps";
import { formatDate, formatEUR } from "@/lib/v2-format";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { TenantDiscountNote } from "../tenant/TenantDiscountNote";
import { RoomBaseDriftNotice } from "../tenant/RoomBaseDriftNotice";
import { isDepositEditLocked } from "@/lib/tenantJourney";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";

type Props = {
  propertyId: number;
  roomId: number;
  bookings: BookingRow[];
};

export function CurrentTenantTab({ propertyId, roomId, bookings }: Props) {
  const { t } = useLanguage();
  // The current tenant = whoever is living in the room *now*: an active booking,
  // or a tenant who has given notice / had their move-out date confirmed but
  // has not left yet. An active booking with a future move-in is "upcoming" and
  // is shown on the History tab instead, so the two tabs never show the same
  // person twice. Both derive from the shared classifier, so this stays in
  // lock-step with the room's Occupied badge.
  const active = bookings.find((b) => roomOccupancyPhase(b) === "current");

  if (!active) {
    return (
      <Card className="border-dashed">
        <EmptyState
          icon={UserRound}
          title={t("admin.empty.noCurrentTenantTitle", "No current tenant")}
          description={t(
            "admin.empty.noCurrentTenantDesc",
            "When a booking becomes active, the tenant appears here. The History tab lists past and upcoming bookings."
          )}
          compact
        />
      </Card>
    );
  }

  return (
    <ActiveBookingCard
      propertyId={propertyId}
      roomId={roomId}
      booking={active}
    />
  );
}

function ActiveBookingCard({
  propertyId,
  roomId,
  booking,
}: {
  propertyId: number;
  roomId: number;
  booking: BookingRow;
}) {
  const { t } = useLanguage();
  const { isSuperAdmin, canActAs, actAsTenant } = useAuth();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile(booking.user_id ?? undefined);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);

  const displayName =
    profile?.full_name ??
    booking.name ??
    profile?.email ??
    booking.email ??
    t("admin.rooms.tenantFallback", "Tenant");
  const displayEmail = profile?.email ?? booking.email;
  // The derived stage — identical inputs to the tenant profile's header pill,
  // so the two surfaces cannot land on different stages for the same tenancy.
  const { journey } = useTenantJourney(booking);
  // Same gap list the profile's "Needs attention" strip shows. Capped at two
  // named fields: this card has one line for it.
  const infoGapLabel = tenantInfoGapLabel(
    {
      name: profile?.full_name ?? booking.name,
      dateOfBirth: profile?.date_of_birth,
      email: displayEmail,
      phone: profile?.phone ?? booking.phone,
    },
    2,
    t
  );
  const initials =
    (displayName === t("admin.rooms.tenantFallback", "Tenant")
      ? "T"
      : displayName
    )
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "T";

  const profileUrl = `/admin/properties/${propertyId}/rooms/${roomId}/tenants/${booking.id}`;
  // The current tenant is by definition a non-archived booking —
  // roomOccupancyPhase excludes archived from "current" — so this is always
  // false here. Archiving simply retires the tenant out of this tab (with an
  // undo toast); there is no archived-but-current state to style.

  const setArchived = useMutation({
    mutationFn: async (next: boolean) => {
      const { error } = await supabase
        .from("bookings")
        .update({ admin_archived_at: next ? new Date().toISOString() : null })
        .eq("id", booking.id);
      if (error) throw error;
    },
    onSuccess: (_, next) => {
      queryClient.invalidateQueries({
        queryKey: ["v2", "bookings", "by-room", roomId],
      });
      invalidateOccupancyViews(queryClient);
      // Undo, matching the profile's archive-undo pattern.
      toast.success(
        next
          ? t("admin.rooms.movedToArchive", "Moved to archive")
          : t("admin.rooms.restoredToPipeline", "Restored to pipeline"),
        {
          action: {
            label: t("admin.rooms.undo", "Undo"),
            onClick: () => setArchived.mutate(!next),
          },
        }
      );
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { message?: string })?.message ||
          t(
            "admin.rooms.couldNotUpdateArchiveStatus",
            "Could not update archive status"
          )
      ),
  });

  return (
    <>
      <Card className="p-5">
        <div className="flex items-start gap-4">
          <Avatar className="h-12 w-12">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={profileUrl}
                className="inline-flex items-center gap-1 font-semibold text-base hover:text-primary hover:underline underline-offset-2 rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {displayName}
                <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
              </Link>
              {journey ? (
                <TenantJourneyStageBadge
                  stageId={journey.stageId}
                  terminalStatus={journey.terminalStatus}
                />
              ) : (
                // The stage needs its evidence rows; until they land, show a
                // placeholder rather than a status the profile might contradict.
                <span className="inline-flex h-[22px] w-28 animate-pulse rounded-full bg-muted" />
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              {t(
                "admin.rooms.clickNameToOpenProfile",
                "Click the name to open the full tenant profile."
              )}
            </p>
            {infoGapLabel && (
              <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                {infoGapLabel}
              </p>
            )}
            {displayEmail && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {displayEmail}
              </p>
            )}

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
              <Field
                label={t("admin.field.moveIn", "Move-in")}
                value={
                  formatDate(
                    booking.actual_move_in_date ??
                      booking.move_in_date ??
                      booking.start_date
                  ) ?? "—"
                }
              />
              <Field
                label={t("admin.field.moveOut", "Move-out")}
                value={
                  formatDate(
                    booking.actual_move_out_date ??
                      booking.move_out_date ??
                      booking.end_date
                  ) ?? "—"
                }
              />
              <Field
                label={t("admin.field.monthlyRent", "Monthly rent")}
                value={formatEUR(booking.monthly_rent) ?? "—"}
              />
              <Field
                label={t("admin.field.deposit", "Deposit")}
                value={formatEUR(booking.deposit_amount) ?? "—"}
              />
            </dl>
            {/* These figures are the agreed base; the note says what the tenant
                actually pays and why (COL-360). */}
            <TenantDiscountNote booking={booking} className="mt-3" />
            {/* The room is the source of truth for the base. If this booking's
                copy has drifted from it (a move that predates the re-sync, a
                hand-typed figure), say so here — next to the numbers — and
                offer the repair rather than leaving the mismatch to be noticed
                by eye. Until the stage is known the deposit is treated as
                locked, so an unloaded journey can never flag a settled one. */}
            <RoomBaseDriftNotice
              bookingId={booking.id}
              roomId={roomId}
              bookingRent={booking.monthly_rent}
              bookingDeposit={booking.deposit_amount}
              depositLocked={
                journey ? isDepositEditLocked(journey.stageId) : true
              }
              canRepair={isSuperAdmin}
              className="mt-3"
            />
          </div>

          <TenantActionsMenu
            profileUrl={profileUrl}
            isSuperAdmin={isSuperAdmin}
            canActAs={canActAs}
            archived={false}
            busy={setArchived.isPending}
            onActAsTenant={() => {
              // Impersonation needs a real auth user. Guest bookings (no
              // linked account) can't be signed into — tell the admin why
              // instead of failing silently.
              if (booking.user_id) void actAsTenant(booking.user_id);
              else
                toast.info(
                  t(
                    "admin.rooms.noAccountToActAs",
                    "{{name}} hasn't created an account yet, so there's no login to act as."
                  ).replace("{{name}}", displayName)
                );
            }}
            onReassign={() => setReassignOpen(true)}
            onToggleArchive={() => setConfirmArchiveOpen(true)}
          />
        </div>
      </Card>

      {isSuperAdmin && (
        <ReassignRoomDialog
          open={reassignOpen}
          onOpenChange={setReassignOpen}
          initialBookingId={booking.id}
          initialTenantName={displayName}
          initialFromRoomId={roomId}
          propertyId={propertyId}
        />
      )}

      <AlertDialog
        open={confirmArchiveOpen}
        onOpenChange={setConfirmArchiveOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.rooms.archiveAndHideTitle",
                "Archive & hide from this room?"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "admin.rooms.archiveAndHideDesc",
                "{{name}} will be moved to the archive and will no longer appear as this room's current tenant. It stays hidden from the live pipeline until restored. You can undo this right after."
              ).replace("{{name}}", displayName)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => setArchived.mutate(true)}>
              {t("admin.rooms.archive", "Archive")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
